import axios from 'axios';
import { Dispatch } from 'react';
import { Setting } from '../App';
import { CUSTOMER_API_URL } from '../constants';
import { PurchasedProductsApiType } from '../types/api/purchased-products-api-type';
import { RecommendProductVariantsApiType } from '../types/api/recommend-product-variants-api-type';
import { SubscriptionsApiType, SubscriptionType } from '../types/api/subscriptions-api-type';
import { createProductInfo, ProductInfoViewType } from '../types/view/product-info-view-type';
import { RecommendProductInfoViewType } from '../types/view/recommend-product-info-view-type';

export const useSubscriptionLine = (
  setting: Setting,
  setSubscription: Dispatch<SubscriptionType>
) => {
  const updateQuantity = (
    subscriptionContractId: string,
    lineId: string,
    quantity: number,
    success: () => void,
    setSubscriptionContractId: Dispatch<string>,
    setRegisteredProducts: Dispatch<ProductInfoViewType[]>,
    setPausedProducts: Dispatch<ProductInfoViewType[]>,
    setRecommendProducts: Dispatch<RecommendProductInfoViewType[]>,
    failed?: (message: string) => void
  ) => {
    (async () => {
      try {
        await axios.post(
          `${CUSTOMER_API_URL}?action=updateSubscriptionLine`,
          {
            subscriptionId: subscriptionContractId,
            lineId,
            quantity,
          },
          {
            headers: { Authorization: setting.token },
          }
        );

        // 追加後の再リクエスト
        const response = await axios.post(
          `${CUSTOMER_API_URL}?action=getSubscriptions`,
          {},
          { headers: { Authorization: setting.token } }
        );

        const subscriptions = (response.data as SubscriptionsApiType).filter(
          (subscription) => subscription.status === 'ACTIVE' || subscription.status === "PAUSED"
        );
        if (subscriptions.length === 0) return;

        const subscription = subscriptions[0];
        setSubscription(subscription);
        setSubscriptionContractId(subscription.subscriptionContractId);

        // お届け予定商品一覧取得、Hooksで更新
        const products = subscription.subscriptionLines.map((subscriptionLine) => {
          return createProductInfo(subscriptionLine, true);
        });
        setRegisteredProducts(products);

        // 購入済み商品一覧取得
        const purchasedProductsResponse = await axios.post(
          `${CUSTOMER_API_URL}?action=getPurchasedProducts`,
          {},
          { headers: { Authorization: setting.token } }
        );

        // 停止中商品一覧取得
        const pausedProducts = (purchasedProductsResponse.data as PurchasedProductsApiType).reduce(
          (result, purchasedProduct) => {
            // 商品に対して、プランが1つのみなので、sellingPlanIdを考慮せず、variantIdのみで判定する
            if (products.every((product) => product.variantId !== purchasedProduct.variantId)) {
              result.push({
                productTitle: purchasedProduct.productTitle,
                image: purchasedProduct.image,
                variantTitle: purchasedProduct.variantTitle,
                originalPrice: purchasedProduct.originalPrice,
                price: purchasedProduct.price,
                quantity: 0,
                variantId: purchasedProduct.variantId,
                planId: purchasedProduct.planId,
                url: purchasedProduct.url,
                active: false,
              });
            }

            return result;
          },
          [] as ProductInfoViewType[]
        );

        // 停止中商品一覧をHooksで更新
        setPausedProducts(pausedProducts);

        // ピックアップ商品一覧を取得
        const recommendProductsResponse = await axios.post(
          `${CUSTOMER_API_URL}?action=getRecommendProductVariants`,
          {},
          { headers: { Authorization: setting.token } }
        );

        const recommendProducts = (recommendProductsResponse.data as RecommendProductVariantsApiType).reduce(
          (result, recommendProduct) => {
            // Mammababyは商品に対して、プランが1つのみなので、sellingPlanIdを考慮せず、variantIdのみで判定する
            if (products.every((product) => product.variantId !== recommendProduct.variantId)) {
              result.push({
                productTitle: recommendProduct.productTitle,
                image: recommendProduct.image,
                variantTitle: recommendProduct.variantTitle,
                originalPrice: recommendProduct.originalPrice,
                price: recommendProduct.price,
                quantity: 0,
                variantId: recommendProduct.variantId,
                planId: recommendProduct.planId,
                message: recommendProduct.message,
                url: recommendProduct.url,
                active: false,
              });
            }

            return result;
          },
          [] as RecommendProductInfoViewType[]
        );

        // ピックアップ商品一覧をHooksで更新
        setRecommendProducts(recommendProducts);

        success();
      } catch (e) {
        if (failed) {
          failed(e.message);
        }
      }
    })();
  };

  const removeProduct = (
    subscriptionContractId: string,
    lineId: string,
    success: () => void,
    setSubscriptionContractId: Dispatch<string>,
    setRegisteredProducts: Dispatch<ProductInfoViewType[]>,
    setPausedProducts: Dispatch<ProductInfoViewType[]>,
    setRecommendProducts: Dispatch<RecommendProductInfoViewType[]>,
    failed?: (message: string) => void
  ) => {
    (async () => {
      try {
        await axios.post(
          `${CUSTOMER_API_URL}?action=removeSubscriptionLine`,
          {
            subscriptionId: subscriptionContractId,
            subscriptionLine: {
              lineId: lineId,
            },
          },
          {
            headers: { Authorization: setting.token },
          }
        );
        console.log('removeProductSuccess!');

        // 追加後の再リクエスト
        const response = await axios.post(
          `${CUSTOMER_API_URL}?action=getSubscriptions`,
          {},
          { headers: { Authorization: setting.token } }
        );

        const subscriptions = (response.data as SubscriptionsApiType).filter(
          (subscription) => subscription.status === 'ACTIVE' || subscription.status === "PAUSED"
        );
        if (subscriptions.length === 0) return;

        const subscription = subscriptions[0];
        setSubscription(subscription);
        setSubscriptionContractId(subscription.subscriptionContractId);

        // お届け予定商品一覧取得、Hooksで更新
        const products = subscription.subscriptionLines.map((subscriptionLine) => {
          return createProductInfo(subscriptionLine, true);
        });
        setRegisteredProducts(products);

        // 購入済み商品一覧取得
        const purchasedProductsResponse = await axios.post(
          `${CUSTOMER_API_URL}?action=getPurchasedProducts`,
          {},
          { headers: { Authorization: setting.token } }
        );

        // 停止中商品一覧取得
        const pausedProducts = (purchasedProductsResponse.data as PurchasedProductsApiType).reduce(
          (result, purchasedProduct) => {
            // 商品に対して、プランが1つのみなので、sellingPlanIdを考慮せず、variantIdのみで判定する
            if (products.every((product) => product.variantId !== purchasedProduct.variantId)) {
              result.push({
                productTitle: purchasedProduct.productTitle,
                image: purchasedProduct.image,
                variantTitle: purchasedProduct.variantTitle,
                originalPrice: purchasedProduct.originalPrice,
                price: purchasedProduct.price,
                quantity: 0,
                variantId: purchasedProduct.variantId,
                planId: purchasedProduct.planId,
                url: purchasedProduct.url,
                active: false,
              });
            }

            return result;
          },
          [] as ProductInfoViewType[]
        );

        // 停止中商品一覧をHooksで更新
        setPausedProducts(pausedProducts);

        // ピックアップ商品一覧を取得
        const recommendProductsResponse = await axios.post(
          `${CUSTOMER_API_URL}?action=getRecommendProductVariants`,
          {},
          { headers: { Authorization: setting.token } }
        );

        const recommendProducts = (recommendProductsResponse.data as RecommendProductVariantsApiType).reduce(
          (result, recommendProduct) => {
            // Mammababyは商品に対して、プランが1つのみなので、sellingPlanIdを考慮せず、variantIdのみで判定する
            if (products.every((product) => product.variantId !== recommendProduct.variantId)) {
              result.push({
                productTitle: recommendProduct.productTitle,
                image: recommendProduct.image,
                variantTitle: recommendProduct.variantTitle,
                originalPrice: recommendProduct.originalPrice,
                price: recommendProduct.price,
                quantity: 0,
                variantId: recommendProduct.variantId,
                planId: recommendProduct.planId,
                message: recommendProduct.message,
                url: recommendProduct.url,
                active: false,
              });
            }

            return result;
          },
          [] as RecommendProductInfoViewType[]
        );

        // ピックアップ商品一覧をHooksで更新
        setRecommendProducts(recommendProducts);

        console.log('fetchFunc() here');
        success();
      } catch (e) {
        if (failed) {
          failed(e.message);
        }
      }
    })();
  };

  const addProduct = (
    planId: string,
    variantId: string,
    quantity: number,
    success: (showModal: boolean) => void,
    setSubscriptionContractId: Dispatch<string>,
    setRegisteredProducts: Dispatch<ProductInfoViewType[]>,
    setPausedProducts: Dispatch<ProductInfoViewType[]>,
    setRecommendProducts: Dispatch<RecommendProductInfoViewType[]>,
    failed?: (message: string) => void
  ) => {
    (async () => {
      try {
        await axios.post(
          `${CUSTOMER_API_URL}?action=addSubscriptionLineToFirstSubscription`,
          {
            planId,
            variantId,
            quantity,
          },
          {
            headers: { Authorization: setting.token },
          }
        );

        // 追加後の再リクエスト
        const response = await axios.post(
          `${CUSTOMER_API_URL}?action=getSubscriptions`,
          {},
          { headers: { Authorization: setting.token } }
        );

        const subscriptions = (response.data as SubscriptionsApiType).filter(
          (subscription) => subscription.status === 'ACTIVE' || subscription.status === 'PAUSED'
        );
        if (subscriptions.length === 0) return;

        const subscription = subscriptions[0];
        setSubscription(subscription);
        setSubscriptionContractId(subscription.subscriptionContractId);

        // お届け予定商品一覧取得、Hooksで更新
        const products = subscription.subscriptionLines.map((subscriptionLine) => {
          return createProductInfo(subscriptionLine, true);
        });
        setRegisteredProducts(products);

        // 購入済み商品一覧取得
        const purchasedProductsResponse = await axios.post(
          `${CUSTOMER_API_URL}?action=getPurchasedProducts`,
          {},
          { headers: { Authorization: setting.token } }
        );

        // 停止中商品一覧取得
        const pausedProducts = (purchasedProductsResponse.data as PurchasedProductsApiType).reduce(
          (result, purchasedProduct) => {
            // 商品に対して、プランが1つのみなので、sellingPlanIdを考慮せず、variantIdのみで判定する
            if (products.every((product) => product.variantId !== purchasedProduct.variantId)) {
              result.push({
                productTitle: purchasedProduct.productTitle,
                image: purchasedProduct.image,
                variantTitle: purchasedProduct.variantTitle,
                originalPrice: purchasedProduct.originalPrice,
                price: purchasedProduct.price,
                quantity: 0,
                variantId: purchasedProduct.variantId,
                planId: purchasedProduct.planId,
                url: purchasedProduct.url,
                active: false,
              });
            }

            return result;
          },
          [] as ProductInfoViewType[]
        );

        // 停止中商品一覧をHooksで更新
        setPausedProducts(pausedProducts);

        // ピックアップ商品一覧を取得
        const recommendProductsResponse = await axios.post(
          `${CUSTOMER_API_URL}?action=getRecommendProductVariants`,
          {},
          { headers: { Authorization: setting.token } }
        );

        const recommendProducts = (recommendProductsResponse.data as RecommendProductVariantsApiType).reduce(
          (result, recommendProduct) => {
            // Mammababyは商品に対して、プランが1つのみなので、sellingPlanIdを考慮せず、variantIdのみで判定する
            if (products.every((product) => product.variantId !== recommendProduct.variantId)) {
              result.push({
                productTitle: recommendProduct.productTitle,
                image: recommendProduct.image,
                variantTitle: recommendProduct.variantTitle,
                originalPrice: recommendProduct.originalPrice,
                price: recommendProduct.price,
                quantity: 0,
                variantId: recommendProduct.variantId,
                planId: recommendProduct.planId,
                message: recommendProduct.message,
                url: recommendProduct.url,
                active: false,
              });
            }

            return result;
          },
          [] as RecommendProductInfoViewType[]
        );

        // ピックアップ商品一覧をHooksで更新
        setRecommendProducts(recommendProducts);

        success(true);
      } catch (e) {
        if (failed) {
          failed(e.message);
        }
      }
    })();
  };

  return {
    updateQuantity,
    removeProduct,
    addProduct,
  };
};
